import {
  Box,
  Button,
  Checkbox,
  CheckboxProps,
  Flex,
  Header,
  StatusMessage,
  StatusMessageProps,
  styled,
  Text,
  Wizard,
} from '@driftt/dds'
import React, { useCallback, useEffect, useState } from 'react'

import { FastlaneTrackingEvents } from 'constants/fastlane'
import { getIsBotUser } from 'utils/access'
import { trackEvent } from 'utils/tracking'

import { useFLWizardStore } from '../FLCreationWizard.context'
import { StyledStepContainer } from '../FLCreationWizard.styles'
import { useTrackIFrameFailedToLoad } from './FLCreationWizardTest.hooks'
import { triggerConfetti } from './triggerConfetti'

const StyledIframeColumn = styled(Box, {
  height: '100%',
  display: 'grid',
  width: '100%',
  gridTemplateRows: '40px auto',
  overflow: 'auto',
})

/**
 * Test mode events sent from the iframe/widget
 *
 * For more info, see:
 * @see https://github.com/Driftt/widget/blob/b983fd3f639c18b276e6954eb302d4d54e5450f3/packages/Conductor/src/modules/forms/utils.ts#L219
 */
const TestModeEvents = [
  'start',
  'formFound',
  'onSubmit',
  'onSuccess',
  'takeover',
] as const
type TestModeEventsLiteral = typeof TestModeEvents[number]

type TestModeEventStatus = 'pending' | 'complete' | 'error'

const TestModeEventsMap: Record<TestModeEventsLiteral, string> = {
  start: 'Test mode started',
  formFound: 'Form was found',
  onSubmit: 'Form was submitted',
  onSuccess: 'Form data was sent and saved',
  takeover: 'Form did not redirect',
}

const FastlaneTestModeParam = 'fastlaneTestMode'

const TestModeEventsStatusToStatusMessageVariantMap: Record<
  TestModeEventStatus,
  StatusMessageProps['variant']
> = {
  pending: 'archived',
  complete: 'success',
  error: 'error',
}

export const FLCreationWizardTest = () => {
  const { store: wizardStore, updateStore } = useFLWizardStore()

  const [testModeStepsStatus, setTestModeStepsStatus] = useState<
    Record<TestModeEventsLiteral, TestModeEventStatus>
  >(
    TestModeEvents.reduce((acc, curr) => {
      acc[curr] = wizardStore.testSuccess ? 'complete' : 'pending'
      return acc
      // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
    }, {} as Record<TestModeEventsLiteral, TestModeEventStatus>)
  )

  const handleFastlaneTestModeEvents = useCallback(
    (testModeEvent: typeof TestModeEvents[number]) => {
      setTestModeStepsStatus((prev) => ({
        ...prev,
        [testModeEvent]: 'complete',
      }))

      if (testModeEvent === 'takeover') {
        updateStore({
          isManualFL: false,
          testSuccess: true,
        })
        triggerConfetti()
      }
    },
    [updateStore]
  )

  const handleCheckboxChange: CheckboxProps['onChange'] = (checked) => {
    if (checked) {
      updateStore({
        isManualFL: true,
      })
    } else {
      updateStore({
        isManualFL: false,
      })
    }
  }

  const handleStepButtonClick = () => {
    if (wizardStore.isManualFL) {
      trackEvent(
        FastlaneTrackingEvents.TestModeRevertedToManual,
        testModeStepsStatus
      )
    }
  }

  const handleAdminOverride = () => {
    updateStore({
      isManualFL: false,
      testSuccess: true,
    })
  }

  useTrackIFrameFailedToLoad({
    testModeStarted: testModeStepsStatus.start === 'complete',
    formUrl: wizardStore.formUrl ?? '',
  })

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      const data = e.data
      if (data.type === FastlaneTestModeParam) {
        handleFastlaneTestModeEvents(data.event)
      }
    }
    window.addEventListener('message', handleMessage, false)
    return () => {
      window.removeEventListener('message', handleMessage, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const queryOperator =
    !!wizardStore.formUrl && wizardStore.formUrl.indexOf('?') >= 0 ? '&' : '?'
  const paramWithValue =
    FastlaneTestModeParam +
    '=' +
    JSON.stringify({
      formType: wizardStore.form?.type,
      formId: wizardStore.form?.id,
    })
  const urlWithTestMode = wizardStore.formUrl + queryOperator + paramWithValue

  const isBotUser = getIsBotUser()

  return (
    <Wizard.Step split="right">
      <StyledStepContainer direction="column" gap={3} alignItems="flex-start">
        <Header variant="h3">Almost there! Let&apos;s test your form.</Header>
        <Text>
          Submit your form on the right so we can identify the best setup method
          for you.
        </Text>
        <Text>
          The playbook is not live, and data captured here won&apos;t affect
          Drift metrics.
        </Text>
        <Text variant="bodyBold">
          If form submissions save to other tools, you will need to delete
          entries made while testing.
        </Text>
        <Flex direction="column" fullWidth>
          {Object.entries(testModeStepsStatus).map(([event, status]) => (
            <StatusMessage
              key={event}
              variant={TestModeEventsStatusToStatusMessageVariantMap[status]}
            >
              {TestModeEventsMap[event as TestModeEventsLiteral]}
            </StatusMessage>
          ))}
        </Flex>
        {Object.values(testModeStepsStatus).every(
          (status) => status === 'complete'
        ) ? (
          <Text>
            Success! Your form works with Quick Setup, our fastest setup method.
          </Text>
        ) : (
          <Flex.Item alignSelf="start">
            <Checkbox
              css={{
                textWrap: 'balance',
              }}
              label="I'm unable to complete the test"
              onChange={handleCheckboxChange}
            />
          </Flex.Item>
        )}
        {isBotUser && (
          <Button onClick={handleAdminOverride}>
            BOT ADMIN: Skip test mode
          </Button>
        )}
        {wizardStore.isManualFL && (
          <Text>
            You can still create the playbook, you'll just need to add
            Javascript to the page.
          </Text>
        )}
        <Wizard.StepActionButton onClick={handleStepButtonClick} />
      </StyledStepContainer>
      <Wizard.StepColumn>
        <StyledIframeColumn>
          <Flex
            css={{
              background: '$blue2',
              borderBottom: '1px solid $blue4',
            }}
            alignItems="center"
            justifyContent="center"
          >
            <StatusMessage variant="info">Fastlane test mode</StatusMessage>
          </Flex>
          <iframe
            title="Test Fastlane on form in customer site using url from previous step"
            style={{
              border: 'none',
            }}
            src={urlWithTestMode}
            width="100%"
            height="100%"
          />
        </StyledIframeColumn>
      </Wizard.StepColumn>
    </Wizard.Step>
  )
}
